// Market-aware Last-N Historical visualization.
//
// This component is descriptive only -- it renders whatever the backend
// returns in ``historical.recent_game_logs``. It NEVER re-derives
// chronology, hit/miss, or push semantics on the client. Every
// observation carries a pre-computed ``hit`` (bool) and ``push`` (bool)
// flag whose comparison logic exactly matches the L5/L10 scorer, so the
// pill row and the historical rates cannot disagree.
//
// Presentation:
//   * Direction is OLDEST -> NEWEST (left -> right). The backend returns
//     newest-first; we reverse a shallow copy purely for display and
//     never for chronology.
//   * Each pill shows the numeric value in the middle. Hit is green,
//     miss is neutral, push is amber and outlined.
//   * A trailing "→ Latest" cue makes the direction unambiguous even for
//     users who can't rely on color.
//   * Accessible: an ordered list with individual aria-labels
//     ("Aug 23 — 1 home run — cleared Over 0.5").
//
// Data protection:
//   * historical_data_stale === true  -> return null (parent renders the
//     amber stale notice; we NEVER paint pills from stale data).
//   * insufficient_data === true or empty logs -> render a subtle
//     "not enough games" fallback.
//   * Partial samples (< 10 games) render exactly the games available
//     and label the section "Last N Games".
//
// This file must stay small. HistoricalPerformanceCard composes it.
import { MARKET_SHORT } from "./constants";

function formatDateShort(iso) {
  if (typeof iso !== "string" || !iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function outcomeLabel({ hit, push, side, line }) {
  const s = side ? side[0].toUpperCase() + side.slice(1) : "";
  const threshold = line !== null && line !== undefined ? ` ${s} ${line}` : "";
  if (push) return `pushed${threshold}`.trim();
  if (hit === true) return `cleared${threshold}`.trim();
  if (hit === false) return `did not clear${threshold}`.trim();
  return "outcome unavailable";
}

function pillStyle({ hit, push }) {
  if (push) {
    return {
      backgroundColor: "#fef3c7",   // amber-100
      color: "#92400e",             // amber-800
      border: "1px solid #f59e0b",
    };
  }
  if (hit === true) {
    return {
      backgroundColor: "#059669",   // emerald-600 (matches PA green)
      color: "#ffffff",
      border: "1px solid #047857",
    };
  }
  return {
    backgroundColor: "#f1f5f9",     // slate-100
    color: "#334155",               // slate-700
    border: "1px solid #cbd5e1",
  };
}

export function RecentGames({ historical, market, side, line }) {
  if (!historical) return null;
  if (historical.historical_data_stale === true) return null;
  if (historical.insufficient_data === true) return null;

  const logsNewestFirst = Array.isArray(historical.recent_game_logs)
    ? historical.recent_game_logs
    : [];
  if (logsNewestFirst.length === 0) {
    return (
      <div className="mt-5" data-testid="analyzer-historical-recent-games-empty">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
          Last games
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Not enough recent games to visualize yet.
        </p>
      </div>
    );
  }

  // Present oldest -> newest so the rightmost pill is the most recent game.
  const displayOrder = logsNewestFirst.slice().reverse();
  const marketShort = MARKET_SHORT[market] || market || "value";
  const label =
    logsNewestFirst.length >= 10
      ? "Last 10 Games"
      : `Last ${logsNewestFirst.length} Game${logsNewestFirst.length === 1 ? "" : "s"}`;

  return (
    <div className="mt-6" data-testid="analyzer-historical-recent-games">
      <div className="flex items-baseline justify-between flex-wrap gap-x-3 gap-y-1">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
          {label}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-display">
          Older → Latest
        </p>
      </div>
      <ol
        className="mt-2 flex flex-wrap gap-1.5"
        aria-label={`${label} — ${marketShort}`}
      >
        {displayOrder.map((g, i) => {
          const dateShort = formatDateShort(g.game_date);
          const outcome = outcomeLabel({
            hit: g.hit, push: g.push, side, line,
          });
          const aria = `${dateShort || "Game"} — ${g.value} ${marketShort} — ${outcome}`;
          const state = g.push
            ? "push"
            : g.hit === true
              ? "hit"
              : g.hit === false
                ? "miss"
                : "unknown";
          return (
            <li
              key={`${g.game_id ?? i}-${g.game_date ?? i}`}
              // index in the DISPLAY order (oldest=0, newest=last)
              data-testid={`analyzer-historical-recent-game-${i}`}
              data-state={state}
              aria-label={aria}
              title={aria}
              className="inline-flex items-center justify-center font-mono-num text-xs h-7 px-2 min-w-[28px] rounded-md"
              style={pillStyle({ hit: g.hit, push: g.push })}
            >
              <span aria-hidden="true">
                {typeof g.value === "number" ? g.value : "—"}
              </span>
              {g.push ? (
                <span aria-hidden="true" className="ml-1 text-[10px]">
                  P
                </span>
              ) : g.hit === true ? (
                <span aria-hidden="true" className="ml-1 text-[10px]">
                  ✓
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
